import type {
  ImpactSector,
  ImpactSectorColor,
  Locales,
  ProjectTheme,
  Theme,
} from '@captaincause/types'

type ImpactSectors = Record<
  ImpactSector,
  {
    label: { [key in Locales]: string }
    color: ImpactSectorColor
    twTextColor: string
    twBgColor: string
  }
>

const IMPACT_SECTORS: ImpactSectors = {
  ecology: {
    label: {
      fr: 'Écologie',
      en: 'Ecology',
      'pt-br': 'Ecologia',
      es: 'Ecología',
      de: 'Ökologie',
    },
    color: 'green',
    twTextColor: 'text-dark-green',
    twBgColor: 'bg-green',
  },
  social: {
    label: {
      fr: 'Social',
      en: 'Social',
      'pt-br': 'Social',
      es: 'Social',
      de: 'Soziales',
    },
    color: 'purple',
    twTextColor: 'text-dark-purple',
    twBgColor: 'bg-purple',
  },
}

export function getImpactSector(impactSector: ImpactSector | undefined) {
  if (!impactSector) return undefined

  return IMPACT_SECTORS[impactSector]
}

export const THEMES = {
  fr: {
    sensibilization: 'Sensibilisation',
    biodiversity: 'Biodiversité',
    circular_economy: 'Zéro déchet',
    climate: 'Climat',
    research: 'Recherche',
    handicap: 'Handicap',
    youth: 'Enfance',
    food: 'Alimentation',
    mental_health: 'Santé mentale',
    elderly: 'Bien vieillir',
    international_solidarity: 'Solidarité internationale',
    diversity: 'Diversité',
    social_action: 'Solidarité',
    education: 'Éducation',
    insertion: 'Insertion',
    women_rights: 'Droits des femmes',
    decontamination: 'Dépollution',
  },
  en: {
    sensibilization: 'Sensibilization',
    biodiversity: 'Biodiversity',
    circular_economy: 'Circular economy',
    climate: 'Climate',
    research: 'Research',
    handicap: 'Handicap',
    youth: 'Youth',
    food: 'Food',
    mental_health: 'Mental health',
    elderly: 'Elderly',
    international_solidarity: 'International solidarity',
    diversity: 'Diversity',
    social_action: 'Solidarity',
    education: 'Education',
    insertion: 'Insertion',
    women_rights: 'Women rights',
    decontamination: 'Decontamination',
  },
  'pt-br': {
    sensibilization: 'Sensibilização',
    biodiversity: 'Biodiversidade',
    circular_economy: 'Economia circular',
    climate: 'Clima',
    research: 'Pesquisa',
    handicap: 'Deficiência',
    youth: 'Juventude',
    food: 'Alimentação',
    mental_health: 'Saúde mental',
    elderly: 'Idosos',
    international_solidarity: 'Solidariedade internacional',
    diversity: 'Diversidade',
    social_action: 'Solidariedade',
    education: 'Educação',
    insertion: 'Inserção',
    women_rights: 'Direitos das mulheres',
    decontamination: 'Descontaminação',
  },
  es: {
    sensibilization: 'Sensibilización',
    biodiversity: 'Biodiversidad',
    circular_economy: 'Economía circular',
    climate: 'Climate',
    research: 'Investigación',
    handicap: 'Minusvalía',
    youth: 'Juventud',
    food: 'Alimentacion',
    mental_health: 'Salud mental',
    elderly: 'Ancianos',
    international_solidarity: 'Solidaridad internacional',
    diversity: 'Diversidad',
    social_action: 'Solidaridad',
    education: 'Educación',
    insertion: 'Inserción',
    women_rights: 'Derechos de la mujer',
    decontamination: 'Descontaminación',
  },
  de: {
    sensibilization: 'Sensibilisierung',
    biodiversity: 'Biologische Vielfalt',
    circular_economy: 'Kreislaufwirtschaft',
    climate: 'Klima',
    research: 'Forschung',
    handicap: 'Handicap',
    youth: 'Jugend',
    food: 'Ernährung',
    mental_health: 'Psychische Gesundheit',
    elderly: 'Ältere Menschen',
    international_solidarity: 'Internationale Solidarität',
    diversity: 'Vielfalt',
    social_action: 'Solidarität',
    education: 'Bildung',
    insertion: 'Eingliederung',
    women_rights: 'Rechte der Frauen',
    decontamination: 'Dekontaminierung',
  },
} as const

export function getTheme(
  theme: ProjectTheme,
  locale: Locales,
): ProjectTheme & { label: string } {
  return {
    ...theme,
    label: THEMES[locale][theme.theme],
  }
}

export function getThemeLabel(theme: Theme, locale: Locales): string {
  return THEMES[locale][theme]
}
